/**
 * I18nManager - Internationalization manager for widget translations
 */
export default class I18nManager {
    constructor(config = {}) {
        this.currentLanguage = config.language || 'en';
        this.availableLanguages = config.availableLanguages || ['en', 'de'];
        this.translations = {};

        this._loadTranslations();
    }

    /**
     * Load built-in translations
     */
    _loadTranslations() {
        this.translations = {
            en: {
                // Widget
                'widget.title': 'Accessibility',
                'widget.open': 'Open Accessibility Menu',
                'widget.close': 'Close',
                'widget.reset': 'Reset All',
                'widget.reset_confirm': 'Reset all accessibility settings?',
                'widget.powered_by': 'Powered by DataHorizon',
                'widget.language': 'Language',

                // Groups
                'group.profiles': 'Accessibility Profiles',
                'group.navigation': 'Navigation',
                'group.navigation_content': 'Navigation & Content',
                'group.color': 'Color Adjustments',
                'group.content': 'Content Adjustments',
                'group.additional': 'Additional Features',

                // Profiles
                'profile.vision_impaired': 'Vision Impaired',
                'profile.vision_impaired.desc': 'Enhances visuals for better visibility',
                'profile.cognitive': 'Cognitive & Learning',
                'profile.cognitive.desc': 'Assists with reading and focus',
                'profile.blindness': 'Blindness',
                'profile.blindness.desc': 'Optimized for screen readers and keyboard navigation',
                'profile.elder': 'Elder Friendly',
                'profile.elder.desc': 'Larger text, clear fonts and enhanced visibility',
                'profile.motor_impaired': 'Motor Impaired',
                'profile.motor_impaired.desc': 'Improves navigation for limited mobility',
                'profile.seizure_safe': 'Seizure Safe',
                'profile.seizure_safe.desc': 'Reduces flashing and motion',

                // Navigation features
                'feature.screen_reader': 'Screen Reader',
                'feature.keyboard_navigation': 'Keyboard Navigation',
                'feature.text_reader': 'Text Reader',
                'feature.read_focus': 'Read Focus',

                // Color features
                'feature.monochrome': 'Monochrome',
                'feature.dark_high_contrast': 'Dark Contrast',
                'feature.bright_high_contrast': 'Bright Contrast',
                'feature.low_saturation': 'Low Saturation',
                'feature.high_saturation': 'High Saturation',
                'feature.contrast_mode': 'Contrast Mode',
                'feature.custom_colors': 'Custom Colors',

                // Content features
                'feature.font_sizing': 'Font Size',
                'feature.font_size': 'Font Size',
                'feature.line_spacing': 'Line Spacing',
                'feature.word_spacing': 'Word Spacing',
                'feature.letter_spacing': 'Letter Spacing',
                'feature.cursor_customization': 'Cursor',

                // Additional features
                'feature.reading_guide': 'Reading Guide',
                'feature.magnifier': 'Magnifier',
                'feature.readable_font': 'Readable Font',
                'feature.image_descriptions': 'Image Descriptions',
                'feature.highlight_links': 'Highlight Links',
                'feature.highlight_headers': 'Highlight Headers',
                'feature.highlight_elements': 'Highlight Elements',
                'feature.enlarge_buttons': 'Enlarge Buttons',
                'feature.readable_mode': 'Readable Mode',
                'feature.mute_media': 'Mute Media',

                // Reading Guide types
                'reading_guide.horizontal': 'Horizontal Line',
                'reading_guide.ruler': 'Ruler',
                'reading_guide.spotlight': 'Spotlight',
                'reading_guide.color': 'Guide Color',
                'reading_guide.thickness': 'Thickness',
                'reading_guide.opacity': 'Opacity',

                // Color customization
                'color.background': 'Background',
                'color.text': 'Text',
                'color.links': 'Links',

                // Control bar
                'control.volume': 'Volume',
                'control.play_pause': 'Play/Pause',
                'control.previous': 'Previous Section',
                'control.next': 'Next Section',
                'control.settings': 'Settings',
                'control.progress': 'Reading Progress',
                'control.hide': 'Hide Widget',

                // Actions
                'action.increase': 'Increase',
                'action.decrease': 'Decrease',
                'action.default': 'Default',
                'action.enable': 'Enable',
                'action.disable': 'Disable',
                'action.on': 'On',
                'action.off': 'Off',

                // Adjustment controls
                'group.adjustments': 'Text Adjustments',
                'adjust.font_size': 'Font Size',
                'adjust.line_spacing': 'Line Spacing',
                'adjust.letter_spacing': 'Letter Spacing',
                'adjust.word_spacing': 'Word Spacing',

                // Hide widget
                'hide.title': 'Hide Accessibility Widget?',
                'hide.message': 'You can restore it by pressing Ctrl+Alt+A or clicking the restore button.',
                'hide.confirm': 'Yes, Hide',
                'hide.cancel': 'Cancel',
                'hide.restore': 'Restore Accessibility Widget'
            },

            de: {
                // Widget
                'widget.title': 'Barrierefreiheit',
                'widget.open': 'Barrierefreiheitsmenü öffnen',
                'widget.close': 'Schließen',
                'widget.reset': 'Alles zurücksetzen',
                'widget.reset_confirm': 'Alle Barrierefreiheitseinstellungen zurücksetzen?',
                'widget.powered_by': 'Bereitgestellt von DataHorizon',
                'widget.language': 'Sprache',

                // Groups
                'group.profiles': 'Barrierefreiheitsprofile',
                'group.navigation': 'Navigation',
                'group.navigation_content': 'Navigation & Inhalt',
                'group.color': 'Farbanpassungen',
                'group.content': 'Inhaltsanpassungen',
                'group.additional': 'Zusätzliche Funktionen',

                // Profiles
                'profile.vision_impaired': 'Sehschwäche',
                'profile.vision_impaired.desc': 'Verbessert die Sichtbarkeit',
                'profile.cognitive': 'Kognitiv & Lernen',
                'profile.cognitive.desc': 'Hilft beim Lesen und Fokussieren',
                'profile.blindness': 'Blindheit',
                'profile.blindness.desc': 'Optimiert für Screenreader und Tastaturnavigation',
                'profile.elder': 'Seniorenmodus',
                'profile.elder.desc': 'Größere Schrift, klare Schriftarten und bessere Sichtbarkeit',
                'profile.motor_impaired': 'Motorisch beeinträchtigt',
                'profile.motor_impaired.desc': 'Erleichtert die Bedienung',
                'profile.seizure_safe': 'Anfallsicher',
                'profile.seizure_safe.desc': 'Reduziert Blinken und Bewegung',

                // Navigation features
                'feature.screen_reader': 'Bildschirmleser',
                'feature.keyboard_navigation': 'Tastaturnavigation',
                'feature.text_reader': 'Textvorleser',
                'feature.read_focus': 'Lesefokus',

                // Color features
                'feature.monochrome': 'Monochrom',
                'feature.dark_high_contrast': 'Dunkler Kontrast',
                'feature.bright_high_contrast': 'Heller Kontrast',
                'feature.low_saturation': 'Geringe Sättigung',
                'feature.high_saturation': 'Hohe Sättigung',
                'feature.contrast_mode': 'Kontrastmodus',
                'feature.custom_colors': 'Eigene Farben',

                // Content features
                'feature.font_sizing': 'Schriftgröße',
                'feature.font_size': 'Schriftgröße',
                'feature.line_spacing': 'Zeilenabstand',
                'feature.word_spacing': 'Wortabstand',
                'feature.letter_spacing': 'Buchstabenabstand',
                'feature.cursor_customization': 'Mauszeiger',

                // Additional features
                'feature.reading_guide': 'Lesehilfe',
                'feature.magnifier': 'Lupe',
                'feature.readable_font': 'Lesbare Schrift',
                'feature.image_descriptions': 'Bildbeschreibungen',
                'feature.highlight_links': 'Links hervorheben',
                'feature.highlight_headers': 'Überschriften hervorheben',
                'feature.highlight_elements': 'Elemente hervorheben',
                'feature.enlarge_buttons': 'Schaltflächen vergrößern',
                'feature.readable_mode': 'Lesemodus',
                'feature.mute_media': 'Medien stummschalten',

                // Reading Guide types
                'reading_guide.horizontal': 'Horizontale Linie',
                'reading_guide.ruler': 'Lineal',
                'reading_guide.spotlight': 'Scheinwerfer',
                'reading_guide.color': 'Hilfsfarbe',
                'reading_guide.thickness': 'Dicke',
                'reading_guide.opacity': 'Deckkraft',

                // Color customization
                'color.background': 'Hintergrund',
                'color.text': 'Text',
                'color.links': 'Links',

                // Control bar
                'control.volume': 'Lautstärke',
                'control.play_pause': 'Abspielen/Pausieren',
                'control.previous': 'Vorheriger Abschnitt',
                'control.next': 'Nächster Abschnitt',
                'control.settings': 'Einstellungen',
                'control.progress': 'Lesefortschritt',
                'control.hide': 'Widget ausblenden',

                // Actions
                'action.increase': 'Erhöhen',
                'action.decrease': 'Verringern',
                'action.default': 'Standard',
                'action.enable': 'Aktivieren',
                'action.disable': 'Deaktivieren',
                'action.on': 'Ein',
                'action.off': 'Aus',

                // Adjustment controls
                'group.adjustments': 'Textanpassungen',
                'adjust.font_size': 'Schriftgröße',
                'adjust.line_spacing': 'Zeilenabstand',
                'adjust.letter_spacing': 'Buchstabenabstand',
                'adjust.word_spacing': 'Wortabstand',

                // Hide widget
                'hide.title': 'Barrierefreiheits-Widget ausblenden?',
                'hide.message': 'Sie können es mit Strg+Alt+A oder der Wiederherstellungsschaltfläche wiederherstellen.',
                'hide.confirm': 'Ja, ausblenden',
                'hide.cancel': 'Abbrechen',
                'hide.restore': 'Barrierefreiheits-Widget wiederherstellen'
            }
        };
    }

    /**
     * Translate a key
     */
    t(key, fallback = null) {
        const lang = this.translations[this.currentLanguage];
        if (lang && lang[key]) {
            return lang[key];
        }
        // Fallback to English
        if (this.translations.en && this.translations.en[key]) {
            return this.translations.en[key];
        }
        return fallback || key;
    }

    /**
     * Set current language
     */
    setLanguage(lang) {
        if (this.availableLanguages.includes(lang)) {
            this.currentLanguage = lang;
            return true;
        }
        return false;
    }

    /**
     * Get current language
     */
    getLanguage() {
        return this.currentLanguage;
    }

    /**
     * Get available languages
     */
    getAvailableLanguages() {
        return this.availableLanguages;
    }

    /**
     * Add custom translations
     */
    addTranslations(lang, translations) {
        if (!this.translations[lang]) {
            this.translations[lang] = {};
        }
        Object.assign(this.translations[lang], translations);
    }
}
